/**
 * AbilityLifecycleMonitor — timeline view of cast lifecycle events,
 * grouped per-cast.
 *
 * Modernized with theme tokens, Badge primitives, and Button controls.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { LagAttributionTooltip } from '../components/LagAttributionTooltip';
import {
  useCastLifecycleStream,
  type CastGroup,
  type CastTimingBreakdown,
} from '../hooks/useCastLifecycleStream';
import { abilityRegistry } from '../../systems/player/abilities/AbilityRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import { Button, Badge } from '../primitives';
import { COLORS, FONT_SIZE, RADII } from '../theme';
import {
  lifecycleStyles as styles,
  statusTone,
  statusGlyph,
  categoryColor,
} from './lifecycleStyles';

function abilityDisplayLabel(abilityId: string): { name: string; id: string } {
  const meta = abilityRegistry.getMetadata(abilityId);
  return { name: meta?.name || abilityId, id: abilityId };
}

function timingColor(ms: number): string {
  if (ms < 50) return '#51cf66';
  if (ms < 150) return '#ffd23f';
  return '#ff5f5f';
}

function TimingPill({ label, value }: { label: string; value: number }): React.ReactElement {
  return (
    <span style={{
      display: 'inline-block',
      background: 'rgba(255,255,255,0.06)',
      padding: '2px 6px',
      borderRadius: RADII.sm,
      fontSize: 10,
      color: timingColor(value),
      fontVariantNumeric: 'tabular-nums',
    }}>
      {label}: {value}ms
    </span>
  );
}

function TimingBreakdownView({ breakdown }: { breakdown?: CastTimingBreakdown }): React.ReactElement | null {
  if (!breakdown) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4, marginBottom: 4 }}>
      <TimingPill label="input→predict" value={breakdown.inputToPredictionMs} />
      <TimingPill label="predict→confirm" value={breakdown.predictionToServerConfirmMs} />
      {breakdown.confirmToVfxMs !== null && (
        <TimingPill label="confirm→VFX" value={breakdown.confirmToVfxMs} />
      )}
      <TimingPill label="confirm→FCT" value={breakdown.confirmToFctMs} />
      <span style={{
        display: 'inline-block',
        background: 'rgba(255,255,255,0.06)',
        padding: '2px 6px',
        borderRadius: RADII.sm,
        fontSize: 10,
        color: COLORS.textMuted,
        fontVariantNumeric: 'tabular-nums',
      }}>
        total: {breakdown.totalMs}ms
      </span>
    </div>
  );
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour12: false });
}

/**
 * Serialize the visible cast groups to a clipboard-friendly string.
 */
export function serializeGroupsForCopy(groups: CastGroup[]): string {
  if (groups.length === 0) return '(no casts)';
  return groups
    .map((g) => {
      const { name, id } = abilityDisplayLabel(g.abilityId);
      const head = `${formatTime(g.startTime)}  ${statusGlyph(g.status)} ${name} (${id})  ${g.status}` +
        (g.rejection ? ` — ${g.rejection.gateName}: ${g.rejection.reason}` : '');
      const lines = g.events.map((e) => {
        const dt = e.time - g.startTime;
        const tag = `${e.category}:${e.label}`;
        const detail = e.detail ? ` — ${e.detail}` : '';
        return `  +${dt}ms  ${tag}${detail}`;
      });
      const anomalies = g.anomalies.map((a) => `  ! ${a.severity}: ${a.type} — ${a.message}`);
      return [head, ...lines, ...anomalies].join('\n');
    })
    .join('\n\n');
}

function GroupCardView({
  group,
  expanded,
  onToggle,
  onContextMenu,
}: {
  group: CastGroup;
  expanded: boolean;
  onToggle: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}) {
  const elapsedMs = group.endTime
    ? group.endTime - group.startTime
    : Date.now() - group.startTime;
  const { name, id } = abilityDisplayLabel(group.abilityId);

  return (
    <div onContextMenu={onContextMenu} style={styles.groupCard}>
      <button
        type="button"
        onClick={onToggle}
        style={styles.groupHeader}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ color: COLORS.textMuted, width: 12, fontSize: 10 }}>{expanded ? '▼' : '▶'}</span>
          <Badge tone={statusTone(group.status)} size="sm">
            {statusGlyph(group.status)} {group.status.toUpperCase()}
          </Badge>
          <strong style={{ color: COLORS.textPrimary }}>{name}</strong>
          {name !== id && (
            <span style={{ color: COLORS.textMuted, fontSize: 10, fontFamily: 'monospace' }}>· {id}</span>
          )}
          {group.anomalies.length > 0 && (
            <Badge tone="warning" size="sm">
              ⚠ {group.anomalies.length}
            </Badge>
          )}
        </span>
        <span style={{ color: COLORS.textMuted, fontSize: 10, whiteSpace: 'nowrap', marginLeft: 8, fontFamily: 'monospace' }}>
          {formatTime(group.startTime)} · {elapsedMs}ms · {group.events.length}ev
        </span>
      </button>

      {expanded && (
        <div style={styles.groupDetails}>
          <TimingBreakdownView breakdown={group.timingBreakdown} />
          {group.rejection && (
            <div style={styles.rejectionBanner}>
              <strong>Rejected — {group.rejection.gateName}</strong>: {group.rejection.detail}
            </div>
          )}
          {group.anomalies.map((a, idx) => (
            <div
              key={idx}
              style={{
                color: a.severity === 'error' ? COLORS.danger : COLORS.warning,
                background: a.severity === 'error' ? 'rgba(255, 107, 107, 0.1)' : 'rgba(241, 196, 15, 0.1)',
                padding: '4px 6px',
                borderRadius: RADII.sm,
                marginBottom: 4,
              }}
            >
              ⚠ {a.type}: {a.message}
            </div>
          ))}
          {group.events.map((e, idx) => (
            <div
              key={idx}
              style={{
                color: categoryColor(e.category),
                display: 'flex',
                justifyContent: 'space-between',
                padding: '2px 0',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span>
                +{e.time - group.startTime}ms · [{e.category}] {e.label}
                {e.detail && <span style={{ color: COLORS.textMuted }}> — {e.detail}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const AbilityLifecycleMonitorContent: React.FC = () => {
  const { groups, paused, pause, resume, clear } = useCastLifecycleStream();
  const [filter, setFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);

  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    group: CastGroup;
  } | null>(null);

  const handleCardContextMenu = useCallback((e: React.MouseEvent, group: CastGroup) => {
    e.preventDefault();
    if (!group.timingBreakdown) return;
    setTooltip({ x: e.clientX + 12, y: e.clientY, group });
  }, []);

  useEffect(() => {
    if (!tooltip) return;
    const handler = () => setTooltip(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [tooltip]);

  const filtered = filter
    ? groups.filter((g) => {
        const name = abilityDisplayLabel(g.abilityId).name.toLowerCase();
        return g.abilityId.toLowerCase().includes(filter.toLowerCase()) ||
          name.includes(filter.toLowerCase());
      })
    : groups;

  const copyAll = useCallback(() => {
    const text = serializeGroupsForCopy(filtered);
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      (err) => gameLogger.warn('ui', `[AbilityLifecycleMonitor] clipboard write failed: ${String(err)}`),
    );
  }, [filtered]);

  const toggleExpanded = (castId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(castId)) next.delete(castId); else next.add(castId);
      return next;
    });
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.toolbar}>
          <div style={styles.toolbarLeft}>
            <Button
              variant={paused ? 'brass' : 'secondary'}
              size="xs"
              onClick={paused ? resume : pause}
            >
              {paused ? '▶ Resume' : '⏸ Pause'}
            </Button>
            <Button variant="ghost" size="xs" onClick={clear}>
              Clear
            </Button>
            <Button variant="secondary" size="xs" onClick={copyAll}>
              {copied ? '✓ Copied' : '📋 Copy'}
            </Button>
          </div>
          <Badge tone="neutral" size="sm">
            {filtered.length} {filtered.length === 1 ? 'cast' : 'casts'}
          </Badge>
        </div>

        <input
          type="text"
          placeholder="Filter by ability ID or name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            background: 'rgba(0, 0, 0, 0.45)',
            border: `1px solid ${COLORS.borderSubtle}`,
            color: COLORS.textPrimary,
            padding: '5px 8px',
            fontFamily: 'monospace',
            fontSize: FONT_SIZE.xs,
            borderRadius: RADII.sm,
            width: '100%',
            boxSizing: 'border-box',
            outline: 'none',
          }}
        />

        <div style={styles.list}>
          {filtered.length === 0 ? (
            <div style={styles.empty}>
              {paused
                ? 'Paused — resume to capture casts'
                : groups.length === 0
                  ? 'No casts yet — try casting an ability'
                  : 'No matches for filter'}
            </div>
          ) : (
            filtered
              .slice()
              .reverse()
              .map((g) => (
                <GroupCardView
                  key={g.castId}
                  group={g}
                  expanded={expandedIds.has(g.castId)}
                  onToggle={() => toggleExpanded(g.castId)}
                  onContextMenu={(e) => handleCardContextMenu(e, g)}
                />
              ))
          )}
        </div>
      </div>

      {tooltip && tooltip.group.timingBreakdown && (
        <LagAttributionTooltip
          breakdown={tooltip.group.timingBreakdown}
          abilityName={abilityDisplayLabel(tooltip.group.abilityId).name}
          style={{ left: tooltip.x, top: tooltip.y }}
        />
      )}
    </>
  );
};
